import { FC, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { Button } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Subtitle } from "capitalrx-components-2.0/lib/typography";
import { Form, SubmitButton, Textbox, UseFormReturn } from "capitalrx-components-2.0/lib/forms";
import { QuestionnaireListResponse } from "capitalrx-types";

import { QuestionAnswers } from "@/api/interfaces/Auth";
import { QuestionnaireFormContent, PACaseCreateForm } from "@/components";
import { ThemeSelectorContext } from "@/context";
import { ANALYTIC_EVENTS, logEvent } from "@/service/analyticsService";
import { getThemeButton, getThemeURL, getQuestionnaireResponses, prefillQuestionnaireAnswers, validatePhoneNumber, formatPhoneWithExtension } from "@/utils";


export interface PAQuestionnaireForm {
  readonly [key: string]: string | boolean | null;
}

interface FormProps {
  goToNextStep: Function;
  form: UseFormReturn<PACaseCreateForm>;
  questionnaireForm: UseFormReturn<PAQuestionnaireForm>;
  prefillQuestions?: QuestionAnswers;
  showAlternateFaxNumberField: boolean;
}


export const CaseQuestionnaireForm: FC<FormProps> = ({ goToNextStep, form, questionnaireForm, prefillQuestions, showAlternateFaxNumberField }) => {
  const navigate = useNavigate();
  const { primary_color, primary_hover_color, secondary_hover_color } = useContext(ThemeSelectorContext);

  const [questionnaires, _] = useState<QuestionnaireListResponse>(form.getValues("questionnaires"));
  const alternateFaxNumber = questionnaireForm.watch("alternate_fax_number");

  useEffect(() => {
    if (alternateFaxNumber && typeof alternateFaxNumber === "string") {
      const formatted = formatPhoneWithExtension(alternateFaxNumber);
      if (formatted !== alternateFaxNumber) {
        questionnaireForm.setValue("alternate_fax_number", formatted);
      }
    }
  }, [alternateFaxNumber, questionnaireForm]);

  const { three_six_twelve_columns } = useResponsive({
    three_six_twelve_columns: {
      desktop: 4,
      tablet: 6,
      mobile: 12,
    },
  });

  useEffect(() => {
    // INJECT RESPONSES FROM PREFILL
    prefillQuestionnaireAnswers(questionnaireForm, questionnaires, prefillQuestions);
  }, []);

  const {
    isCompressed,
  } = useResponsive({
    isCompressed: {
      desktop: false,
      tablet: false,
      mobile: true,
    },
  });
  const StackOrShelf = isCompressed ? Stack : Shelf;

  const formSubmitted = useCallback(async (data: PAQuestionnaireForm) => {
    const responses = getQuestionnaireResponses(data, questionnaires);
    form.setValue("questionnaire_responses", responses);
    await goToNextStep();
  }, [questionnaires]);

  const goToHome = useCallback(() => {
    // log cancel click
    logEvent(ANALYTIC_EVENTS.PA_CASE_CREATE.cancel);
    navigate(getThemeURL("HOME"));
  }, [navigate]);

  return (
    <Form form={questionnaireForm} onSubmit={formSubmitted}>
      <Stack spacing="large">
        <Stack>
          {
            showAlternateFaxNumberField
            && <>
              <Subtitle>Prescriber Contact</Subtitle>
              <Grid>
                <Grid.Span columns={three_six_twelve_columns}>
                  <Textbox.Field
                    testId="test__alternate_fax_number"
                    label="Alternate Fax Number"
                    name="alternate_fax_number"
                    validator={validatePhoneNumber}
                    instructions="123-456-7890 x 123"
                  />
                </Grid.Span>
              </Grid>
            </>
          }
          {!questionnaires?.results?.length
            && <span>No Clinical Criteria Questions necessary</span>
          }

          {questionnaires?.results?.length > 0 && (
            <Stack>
              <Subtitle>Clinical Criteria Questions</Subtitle>
              {questionnaires.results?.map(({ questionnaire }) => (
                questionnaire.questions?.length && (
                  <QuestionnaireFormContent
                    key={questionnaire.id}
                    answersOpen={true}
                    questionnaire={questionnaire}
                  />
                ) || null
              ))}
            </Stack>
          )}
        </Stack>

        <StackOrShelf>
          <SubmitButton appearance={getThemeButton("primary", primary_color, primary_hover_color)}>
            Submit Case
          </SubmitButton>
          {!questionnaireForm.formState.isSubmitting && (
            <Button appearance={getThemeButton("secondary", primary_color, secondary_hover_color)} onClick={goToHome}>
              Cancel
            </Button>
          )}
          <Shelf.Spacer />
        </StackOrShelf>
      </Stack>
    </Form>
  );
};

export default CaseQuestionnaireForm;
